import { Button, Spinner } from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import { api } from '@/lib/api'

type GoogleCalendarCallbackSearch = {
  code?: string
  state?: string
  error?: string
  error_description?: string
}

export const Route = createFileRoute(
  '/_app/integracoes/google-calendar/callback'
)({
  validateSearch: (
    search: Record<string, unknown>
  ): GoogleCalendarCallbackSearch => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
    error_description:
      typeof search.error_description === 'string'
        ? search.error_description
        : undefined,
  }),
  component: GoogleCalendarCallbackPage,
})

function GoogleCalendarCallbackPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const { currentOrgId } = useAuth()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  )
  const [message, setMessage] = useState('Conectando Google Calendar...')
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    if (!currentOrgId) return

    handledRef.current = true

    async function connect() {
      // O state é sempre "orgId|userId": só existe conexão de calendário
      // pessoal. O userId vai junto apenas para conferência — quem o servidor
      // usa é o da sessão, para ninguém sobrescrever a credencial de um colega.
      const state = search.state || ''
      const orgId = state.split('|')[0] || currentOrgId

      if (search.error) {
        setStatus('error')
        setMessage(
          search.error_description ||
            'A autorização do Google Calendar foi cancelada ou recusada.'
        )
        return
      }

      if (!search.code) {
        setStatus('error')
        setMessage('O Google não retornou o código de autorização.')
        return
      }

      if (!orgId) {
        setStatus('error')
        setMessage('Não foi possível identificar a organização.')
        return
      }

      try {
        await api.post(
          `/orgs/${orgId}/integrations/google-calendar/user-callback`,
          { code: search.code }
        )
        setStatus('success')
        setMessage('Google Calendar pessoal conectado com sucesso.')
        toast.success('Google Calendar conectado')
        window.setTimeout(() => {
          void navigate({ to: '/config', search: { tab: 'calendario' } })
        }, 900)
      } catch {
        setStatus('error')
        setMessage('Não foi possível concluir a conexão com o Google Calendar.')
      }
    }

    void connect()
  }, [currentOrgId, navigate, search])

  return (
    <div className="flex flex-1 items-center justify-center p-6">
      <div className="bg-card w-full max-w-md rounded-xl border p-6 text-center shadow-sm">
        <div className="bg-muted mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
          {status === 'loading' ? (
            <Spinner className="text-muted-foreground h-5 w-5" />
          ) : status === 'success' ? (
            <CheckCircle2 className="text-success h-5 w-5" />
          ) : (
            <AlertCircle className="text-destructive h-5 w-5" />
          )}
        </div>
        <h1 className="text-lg font-semibold">
          {status === 'success'
            ? 'Calendário conectado'
            : status === 'error'
              ? 'Falha na conexão'
              : 'Conectando calendário'}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">{message}</p>
        {status === 'error' ? (
          <Button
            type="button"
            className="mt-5"
            onClick={() => void navigate({ to: '/integracoes' })}
          >
            Voltar para integrações
          </Button>
        ) : null}
      </div>
    </div>
  )
}
